import { client } from "@/hera-api/client/client.gen";
import type { StrDedupListing } from "@/hera-api/str-dedup-api";
import type {
  StrGovernmentLicenseSummary,
  StrListingDetail,
} from "@/hera-api/str-government-api";

const SECURITY = [{ scheme: "bearer", type: "http" }] as const;

export interface StrMappedAddress {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  formatted_address: string | null;
  address_level: "unit" | "building";
  google_place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  location_type: string | null;
}

export interface StrCatalogListing {
  id: string;
  platform: string;
  listing_id: string;
  title: string | null;
  url: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  city: string | null;
  location_status: string;
  property_representative_id: string;
  property_listing_count: number;
  address: StrMappedAddress | null;
  jurisdiction?: string | null;
  regions?: string | null;
  unit_category?: StrUnitCategory | null;
  unit_category_source?: "ai" | "human" | null;
  flags?: string[];
  first_seen_on?: string | null;
}

export type StrUnitCategory =
  | "entire_unit"
  | "one_of_many"
  | "adu"
  | "shared_space"
  | "rv"
  | "tent"
  | "campsite";

export interface StrCatalogMapPin {
  id: string;
  lat: number;
  lng: number;
  display_id: string;
  address: string;
  mapped?: boolean;
  status_label?: string | null;
  platforms?: string[];
  hero_image_url?: string | null;
  pin_color?: string | null;
  compliance?: "compliant" | "non_compliant" | "unmapped" | null;
  address_unverified?: boolean;
}

export interface StrPropertyCatalog {
  representative_id: string;
  listing_count: number;
  platforms: string[];
  title: string | null;
  city: string | null;
  photo_url: string | null;
  bedrooms_min: number | null;
  bedrooms_max: number | null;
  location_status: string;
  related_property_count: number;
  address_status: "mapped" | "building_only" | "conflict" | "unmapped";
  address: StrMappedAddress | null;
  conflicting_addresses: string[];
  jurisdiction?: string | null;
  regions?: string | null;
  unit_category?: StrUnitCategory | null;
  unit_category_source?: "ai" | "human" | null;
  unit_category_mixed?: boolean;
  first_seen_on?: string | null;
}

export interface StrPage<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  show_regions?: boolean;
}

export interface StrAddressInheritance {
  listing_id: string;
  platform: string;
  title: string | null;
  relation: string;
  distance_meters: number | null;
  far_from_source: boolean;
}

export interface StrAddressMappingTarget {
  source_listing_id: string;
  property_representative_id: string;
  listing: StrDedupListing;
  candidates: StrMappedAddress[];
  existing_address: StrMappedAddress | null;
  inheritance: StrAddressInheritance[];
  attom?: StrAttomLookup | null;
  suggestion_latitude?: number | null;
  suggestion_longitude?: number | null;
  match_reasons?: Array<
    | "zillow"
    | "streetview"
    | "satellite"
    | "map_pin"
    | "house_number_visible"
    | "owner_name"
  >;
  match_reason_note?: string | null;
  unit_category?: StrUnitCategory | null;
  unit_category_source?: "ai" | "human" | null;
  unit_category_mixed?: boolean;
  unit_category_listing_count?: number;
  owner_matches?: StrNearbyProperty[];
  mapped_duplicate?: StrMappedDuplicate | null;
}

export interface StrMappedDuplicate {
  pair_id: string;
  score: number;
  listing_id: string;
  platform: string;
  title?: string | null;
  host_name?: string | null;
  mapped_address?: string | null;
  shared_image_count?: number;
}

export interface StrAttomParcel {
  attom_id: string | null;
  apn: string | null;
  county: string | null;
  county_fips: string | null;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  owner_name?: string | null;
  owner_mailing_address?: string | null;
}

export interface StrAttomLookup {
  status: "matched" | "none" | "multiple" | "unavailable";
  attached: boolean;
  match: StrAttomParcel | null;
  candidates: StrAttomParcel[];
  message?: string | null;
}

export interface StrAttomSelection {
  attom_id: string | null;
  county: string | null;
  apn: string | null;
}

export interface StrAddressMappingResult {
  decision_id: string | null;
  inherited_count: number;
  unresolved_pair_count: number;
  attom?: StrAttomLookup | null;
}

export interface StrUnitClassificationSuggestResult {
  classified_count: number;
}

export interface StrUnitCategoryResult {
  category: StrUnitCategory | null;
  source: "ai" | "human" | null;
  updated_count?: number;
  mixed?: boolean;
}

function requireData<T>(
  result: { error?: unknown; data?: T },
  fallback: string,
): T {
  if (result.error) {
    const error = result.error as { detail?: string; message?: string };
    throw new Error(error.detail ?? error.message ?? fallback);
  }
  if (result.data === undefined) throw new Error(fallback);
  return result.data;
}

interface StrCatalogQuery {
  page: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  direction?: "asc" | "desc";
  filters?: string;
  jurisdictionIds?: string[];
  regionIds?: string[];
  includeUnassignedRegions?: boolean;
}

function catalogQuery(options: StrCatalogQuery, fallbackPageSize: number) {
  return {
    page: options.page,
    page_size: options.pageSize ?? fallbackPageSize,
    ...(options.search ? { search: options.search } : {}),
    ...(options.sort ? { sort: options.sort } : {}),
    ...(options.direction ? { direction: options.direction } : {}),
    ...(options.filters ? { filters: options.filters } : {}),
    ...(options.jurisdictionIds?.length
      ? { jurisdiction_ids: options.jurisdictionIds }
      : {}),
    ...(options.regionIds?.length ? { region_ids: options.regionIds } : {}),
    ...(options.includeUnassignedRegions
      ? { include_unassigned_regions: true }
      : {}),
  };
}

async function getAllCatalogPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<StrPage<T>>,
): Promise<T[]> {
  const pageSize = 100;
  const first = await fetchPage(1, pageSize);
  const items = [...first.items];
  const pages = Math.max(1, Math.ceil(first.total / pageSize));
  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchPage(page, pageSize);
    items.push(...next.items);
  }
  return items;
}

export async function getStrListings(
  options: StrCatalogQuery,
): Promise<StrPage<StrCatalogListing>> {
  const result = await client.get<StrPage<StrCatalogListing>>({
    url: "/operator/str-listings",
    query: catalogQuery(options, 25),
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR listings");
}

export async function getAllStrListings(
  options: Omit<StrCatalogQuery, "page" | "pageSize">,
): Promise<StrCatalogListing[]> {
  return getAllCatalogPages((page, pageSize) =>
    getStrListings({ ...options, page, pageSize }),
  );
}

export async function getStrListingMapPins(options: {
  search?: string;
  filters?: string;
}): Promise<StrCatalogMapPin[]> {
  const result = await client.get<{
    200: { pins?: StrCatalogMapPin[] };
  }>({
    url: "/operator/str-listings/map-pins",
    query: {
      ...(options.search ? { search: options.search } : {}),
      ...(options.filters ? { filters: options.filters } : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR listing map pins").pins ?? [];
}

export async function getStrListingFilterValues(options: {
  column: string;
  search?: string;
  filters?: string;
  q?: string;
}): Promise<string[]> {
  const result = await client.get<{
    200: { values?: string[] };
  }>({
    url: "/operator/str-listings/filter-values",
    query: {
      column: options.column,
      ...(options.search ? { search: options.search } : {}),
      ...(options.filters ? { filters: options.filters } : {}),
      ...(options.q ? { q: options.q } : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR listing filters").values ?? [];
}

export async function getStrProperties(
  options: StrCatalogQuery,
): Promise<StrPage<StrPropertyCatalog>> {
  const result = await client.get<StrPage<StrPropertyCatalog>>({
    url: "/operator/str-properties",
    query: catalogQuery(options, 25),
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR properties");
}

export interface StrOperatorPropertyDetail {
  representative_id: string;
  title: string | null;
  address: string | null;
  address_unverified?: boolean;
  regions: string[];
  latitude: number | null;
  longitude: number | null;
  listings: StrListingDetail[];
  licenses: StrGovernmentLicenseSummary[];
}

export async function getStrPropertyDetail(
  representativeId: string,
): Promise<StrOperatorPropertyDetail> {
  const result = await client.get<StrOperatorPropertyDetail>({
    url: `/operator/str-properties/${representativeId}`,
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR property");
}

export async function getAllStrProperties(
  options: Omit<StrCatalogQuery, "page" | "pageSize">,
): Promise<StrPropertyCatalog[]> {
  return getAllCatalogPages((page, pageSize) =>
    getStrProperties({ ...options, page, pageSize }),
  );
}

export async function getStrPropertyMapPins(options: {
  search?: string;
  filters?: string;
  jurisdictionIds?: string[];
  regionIds?: string[];
  includeUnassignedRegions?: boolean;
}): Promise<StrCatalogMapPin[]> {
  const result = await client.get<{
    200: { pins?: StrCatalogMapPin[] };
  }>({
    url: "/operator/str-properties/map-pins",
    query: {
      ...(options.search ? { search: options.search } : {}),
      ...(options.filters ? { filters: options.filters } : {}),
      ...(options.jurisdictionIds?.length
        ? { jurisdiction_ids: options.jurisdictionIds }
        : {}),
      ...(options.regionIds?.length ? { region_ids: options.regionIds } : {}),
      ...(options.includeUnassignedRegions
        ? { include_unassigned_regions: true }
        : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR property map pins").pins ?? [];
}

export async function getStrPropertyFilterValues(options: {
  column: string;
  search?: string;
  filters?: string;
  q?: string;
  jurisdictionIds?: string[];
  regionIds?: string[];
  includeUnassignedRegions?: boolean;
}): Promise<string[]> {
  const result = await client.get<{
    200: { values?: string[] };
  }>({
    url: "/operator/str-properties/filter-values",
    query: {
      column: options.column,
      ...(options.search ? { search: options.search } : {}),
      ...(options.filters ? { filters: options.filters } : {}),
      ...(options.q ? { q: options.q } : {}),
      ...(options.jurisdictionIds?.length
        ? { jurisdiction_ids: options.jurisdictionIds }
        : {}),
      ...(options.regionIds?.length ? { region_ids: options.regionIds } : {}),
      ...(options.includeUnassignedRegions
        ? { include_unassigned_regions: true }
        : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return (
    requireData(result, "Failed to load STR property filters").values ?? []
  );
}

export function addressMappingTargetRequest(options?: {
  listingId?: string;
  propertyListingId?: string;
  listingCity?: string;
}): { url: string; query?: Record<string, string> } {
  const listingId = options?.listingId;
  const propertyListingId = options?.propertyListingId;
  if (listingId || propertyListingId) {
    return {
      url: "/operator/str-address-mapping/target",
      query: {
        ...(listingId ? { listing_id: listingId } : {}),
        ...(propertyListingId
          ? { property_listing_id: propertyListingId }
          : {}),
      },
    };
  }
  const listingCity = options?.listingCity?.trim();
  return {
    url: "/operator/str-address-mapping/next",
    ...(listingCity ? { query: { listing_city: listingCity } } : {}),
  };
}

export async function getStrAddressMappingCities(): Promise<string[]> {
  const result = await client.get<{
    200: { cities?: string[] };
  }>({
    url: "/operator/str-address-mapping/cities",
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load listing cities").cities ?? [];
}

export async function getStrAddressMappingTarget(options?: {
  listingId?: string;
  propertyListingId?: string;
  listingCity?: string;
}): Promise<StrAddressMappingTarget | null> {
  const request = addressMappingTargetRequest(options);
  const result = await client.get<StrAddressMappingTarget | null>({
    url: request.url,
    ...(request.query ? { query: request.query } : {}),
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to load STR address mapping");
}

export async function confirmStrAddressMapping(
  sourceListingId: string,
  address: StrMappedAddress,
  attomResolved = false,
  matchReasons: Array<
    | "zillow"
    | "streetview"
    | "satellite"
    | "map_pin"
    | "house_number_visible"
    | "owner_name"
  > = [],
  matchReasonNote: string | null = null,
): Promise<StrAddressMappingResult> {
  const result = await client.post<StrAddressMappingResult>({
    url: "/operator/str-address-mapping/confirm",
    body: {
      source_listing_id: sourceListingId,
      address,
      attom_resolved: attomResolved,
      match_reasons: matchReasons,
      match_reason_note: matchReasonNote,
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to save STR address");
}

export async function lookupStrAttomParcel(
  sourceListingId: string,
  address: StrMappedAddress,
  selected?: StrAttomSelection | null,
): Promise<StrAttomLookup> {
  const result = await client.post<StrAttomLookup>({
    url: "/operator/str-address-mapping/attom-lookup",
    body: {
      source_listing_id: sourceListingId,
      address,
      ...(selected ? { selected } : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to look up ATTOM parcel");
}

export async function skipStrAddressMapping(
  sourceListingId: string,
): Promise<StrAddressMappingResult> {
  const result = await client.post<StrAddressMappingResult>({
    url: "/operator/str-address-mapping/skip",
    body: { source_listing_id: sourceListingId },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to skip STR address");
}

export interface StrNearbyProperty {
  attom_id: string | null;
  county_fips: string | null;
  apn: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  distance_miles: number | null;
  owner_name?: string | null;
}

export interface StrNearbyHouseNumberResponse {
  listing_id: string;
  house_number: string;
  pin_latitude: number | null;
  pin_longitude: number | null;
  radius_miles: number | null;
  radius_expanded: boolean;
  needs_pin: boolean;
  message: string;
  results: StrNearbyProperty[];
}

export async function getStrAddressMappingNearby(options: {
  listingId: string;
  houseNumber: string;
  radiusMiles?: number;
}): Promise<StrNearbyHouseNumberResponse> {
  const result = await client.get<StrNearbyHouseNumberResponse>({
    url: "/operator/str-address-mapping/nearby",
    query: {
      listing_id: options.listingId,
      house_number: options.houseNumber,
      ...(options.radiusMiles != null
        ? { radius_miles: options.radiusMiles }
        : {}),
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to search properties near the pin");
}

export async function undoStrAddressMapping(): Promise<StrAddressMappingResult> {
  const result = await client.post<StrAddressMappingResult>({
    url: "/operator/str-address-mapping/undo",
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to undo STR address");
}

export async function suggestStrUnitCategories(options: {
  kind: "listings" | "properties";
  filters?: string;
  limit?: number;
}): Promise<StrUnitClassificationSuggestResult> {
  const result = await client.post<StrUnitClassificationSuggestResult>({
    url: "/operator/str-unit-classifications/suggest",
    body: {
      kind: options.kind,
      filters: options.filters ?? null,
      limit: options.limit ?? 15,
    },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to suggest property types");
}

export async function setStrListingUnitCategory(
  listingId: string,
  category: StrUnitCategory | null,
): Promise<StrUnitCategoryResult> {
  const result = await client.put<StrUnitCategoryResult>({
    url: `/operator/str-listings/${listingId}/unit-category`,
    body: { category },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to save property type");
}

export async function setStrPropertyUnitCategory(
  representativeId: string,
  category: StrUnitCategory | null,
): Promise<StrUnitCategoryResult> {
  const result = await client.put<StrUnitCategoryResult>({
    url: `/operator/str-properties/${representativeId}/unit-category`,
    body: { category },
    parseAs: "json",
    security: [...SECURITY],
  });
  return requireData(result, "Failed to save property type");
}
